import * as React from 'react'
import type { LinksFunction, MetaFunction } from 'react-router'
import {
  isRouteErrorResponse,
  Links,
  Outlet,
  Scripts,
  ScrollRestoration,
  useRouteError,
} from 'react-router'

import { DiagnosticsPanel } from '@modules/diagnostics/DiagnosticsPanel/DiagnosticsPanel'
import { Toaster } from '@modules/shadcn/ui/Toast/Toaster'

import { AuthRetryScreen } from '@company/template/AuthRetryScreen/AuthRetryScreen'
import { NotFoundPage } from '@company/template/NotFoundPage/NotFoundPage'

import { useAuth } from './auth/auth'
import { AppStateContainer } from './context/AppStateContainer'
import { ConnectProvider, CustomProvider, WebVitalsProvider } from './context/CustomProvider'

import '@modules/scss/global.css'

const Head = () => {
  return (
    <head>
      <Links />
      <meta charSet="utf-8" />
      <meta content="width=device-width, initial-scale=1" name="viewport" />
      <link href="https://fonts.googleapis.com" rel="preconnect" />
      <link href="https://fonts.gstatic.com" rel="preconnect" />
      <link
        href="https://fonts.googleapis.com/css2?family=Inter:wght@100;400;700;900&display=swap"
        rel="stylesheet"
      />
      <link href="/img/icon_ie.svg" rel="icon" />
      <link href="/img/apple-touch-icon.png" rel="apple-touch-icon" sizes="180x180" />
      <meta content="width=device-width, initial-scale=1.0, maximum-scale=1.0" name="viewport" />
      <meta content="いえサプリ" name="apple-mobile-web-app-title" />
      <meta content="いえサプリ" name="application-name" />

      <meta content="いえサプリ" property="og:title" />
      <meta content="/img/ogp_ie.png" property="og:image" />
      <meta content="いえサプリ" property="og:site_name" />
    </head>
  )
}

export const links: LinksFunction = () => {
  return [
    // { rel: 'stylesheet', href: stylesheet },
  ]
}
export const meta: MetaFunction = () => {
  return [{ charset: 'utf-8' }, { lang: 'ja' }]
}

export const clientLoader = () => {
  return null
}

const Document: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <html lang="ja">
      <Head />
      <body>
        {children}
        <Toaster />
        {/* STG / 開発環境だけで出る診断パネル。認証前（ログイン画面）でも見えるよう
            Document直下に置く。本番では何も描画しない */}
        <DiagnosticsPanel />
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  )
}
const CustomApp = ({ children }: { children: React.ReactNode }) => {
  const { loading, logout, retryAuth } = useAuth()
  const { staff, authError } = AppStateContainer.useContainer()

  if (loading) return false

  // サインインは通ったが、スタッフ情報の取得が一時的な失敗（通信断・サーバ内部エラー）で
  // 終わった状態。何も描かないと「ログインしたのに画面が出ない」になるので再試行画面を出す。
  // 確定的な拒否はサインアウト済みなのでここには来ない（@company/auth/staffMeFailure.ts）
  if (authError !== null && staff === null) {
    return <AuthRetryScreen message={authError} onRetry={retryAuth} onSignOut={logout} />
  }

  return <>{children}</>
}

const App = () => {
  return (
    <Document>
      <CustomProvider>
        <ConnectProvider>
          <CustomApp>
            <WebVitalsProvider />
            <Outlet />
          </CustomApp>
        </ConnectProvider>
      </CustomProvider>
    </Document>
  )
}
export default App

export const HydrateFallback = () => {
  return (
    <Document>
      <div />
      <Scripts />
    </Document>
  )
}

export const ErrorBoundary = () => {
  const error = useRouteError()
  console.error(error)
  // どのルートにも一致しないURL（廃止した `/ai-color-simulation/<案件ID>` など）は404の表示を出す。
  // それ以外のエラーは従来どおり何も描かない。
  const isNotFound = isRouteErrorResponse(error) && error.status === 404
  return (
    <Document>
      {isNotFound && <NotFoundPage />}
      <Scripts />
    </Document>
  )
}
